// Dezi / mKonnekt MANUFACTURER DISCOUNT (QV-7124) — web port of the Android
// `ui/in_store/dezi/*` package (DeziDiscountModels / DeziRequestMapper / DeziInjector) plus the
// OrderCreateViewModel rules that surround it.
//
// A dezi-enabled store looks the current cart up against mKonnekt (`Mkonnekt_discount/get_discounts`,
// the PUBLIC no-auth Node route) and applies the returned manufacturer discounts to the cart.
//
// ── THE FIVE RULES (Android "Wave 6", owner/Altria) ─────────────────────────────────────────────
//  1. DEZI WINS over BOGO / Mix&Match. A upc mKonnekt discounts is excluded from deal eligibility and
//     any deal already on it is reverted to raw before dezi injects.
//  2. The line is labelled "Manufacturer Discount", not "Item Discount".
//  3. Manual $ / % item discount, the item coupon AND the price override are all BLOCKED on a dezi
//     line (override block = Android fix d3).
//  4. An order-level coupon / custom discount NEVER stacks on a dezi line: dezi lines are excluded
//     from the discount BASE and from the per-line distribution. `$` redistributes over the non-dezi
//     lines; `%` applies to the non-dezi subtotal only ("Decision b").
//  5. OTD is rejected outright on any cart containing a dezi line (it is an order-level discount, and
//     "customer pays exactly $X out the door" is not expressible on a partial base).
//
// ── WHY THE WEB IS DERIVED, NOT ACCUMULATED (deliberate divergence, money-equivalent) ────────────
// Android mutates the cart imperatively (clear the previous contribution → re-run deals → re-inject),
// which is why it needs the whole Wave-7/8 "adopt a restored discount" apparatus to avoid
// double-applying a held order's discount. The web engine instead RE-DERIVES the entire cart from
// scratch on every `recalculate()` (see engine/index.js `resetWorkingPrices`), so the injection is a
// pure function of (cart, deziItemDiscounts). Applying twice is therefore impossible by construction,
// and the hold/restore double-apply class of bug cannot exist here. The MONEY (per-upc grouping,
// proportional cent-exact split, headroom clamp, per-line breakdown) is ported line-for-line.

import { roundHalfUp, toNum } from "./money";

export { MKONNEKT_OP_COMPANY_LABELS, opCompanyLabel } from "../../../services/mkonnekt/opCompany";

/** All allocation is done in integer cents so the splits are exact. */
const CENTS = 100;

// ── Gate ────────────────────────────────────────────────────────────────────────────────────────

/**
 * The store gate — `is_dezi_enabled == "1"` AND a non-blank `rcn`, both from
 * `Profile_setup/inventory_register_setting`. `rcn` is a merchant identifier the backend requires
 * non-empty, so a whitespace-only value is INVALID → gate OFF (the safer default for a money
 * feature). Absence-safe: fields missing ⇒ off ⇒ every dezi path below is a no-op.
 */
export function isDeziDiscountEnabled(isDeziEnabledValue, rcnValue) {
  return String(isDeziEnabledValue ?? "") === "1" && String(rcnValue ?? "").trim() !== "";
}

// ── Line identity ───────────────────────────────────────────────────────────────────────────────

/**
 * The upc that identifies a cart line to mKonnekt. **VARIANT-AWARE.**
 *
 * A variant product's real code lives on the VARIANT; the parent product's `upc` is blank once
 * variants are switched on (Add/Edit Product hides the product-level UPC field). Android reads
 * `if (isvarient) vupc else upc` — but its `vupc` is already expanded to ONE variant, whereas the web
 * keeps the comma-joined catalog string on `model` and stamps the chosen variant's own code onto the
 * line as `variantUpc` at add time (uiUtils.buildAddPayload). So the web reads that stamped value.
 *
 * All five behaviours MUST agree on one code — eligibility, the request, the injection match, the
 * greying/BOGO suppression and the tender-wait — which is why this is one accessor, not five inline
 * branches.
 */
export function deziUpcOf(line) {
  if (!line) return "";
  if (line.isVariant) return String(line.variantUpc || "").trim();
  return String((line.model || {}).upc || "").trim();
}

/**
 * True ONLY for a regular product-list item — the sole item type a manufacturer discount may touch.
 * Rejects custom products / custom-category items (`model.id` 0 or `isCustomLineItem`) and any line
 * with no usable upc.
 *
 * ⚠️ QUICK-ADD IS **NOT** EXCLUDED (Android owner change 2026-08-05). A quick-add product is a real
 * catalog product with a mandatory, duplicate-checked upc, so mKonnekt can match it like any other
 * line. The upc guard runs FIRST, so admitting quick-add can only ever admit lines mKonnekt could
 * actually match.
 */
export function isRegularInventoryProduct(line) {
  if (!line) return false;
  if (!deziUpcOf(line)) return false;
  const m = line.model || {};
  if (toNum(m.id) === 0) return false;
  if (m.isCustomLineItem === true || line.isCustomLineItem === true) return false;
  return true;
}

/** Synthetic / non-product rows never carry a manufacturer discount. */
function isSyntheticLine(line) {
  return !!(
    line.isSplitPayment ||
    line.isSplitPaymentDiscount ||
    line.isSplitRefund ||
    line.isLotteryPayout ||
    line.isScratcherPayout ||
    line.isDeziDiscount
  );
}

function isInjectable(line) {
  return !isSyntheticLine(line) && isRegularInventoryProduct(line);
}

// ── Request / response ──────────────────────────────────────────────────────────────────────────

/**
 * The customer's phone as mKonnekt wants it: exactly 10 US digits, or "" when there is no usable
 * number. Non-digits are stripped and a leading country code 1 on an 11-digit number is dropped;
 * anything else (too short/long) is sent as "" rather than as a value that can never match a
 * loyalty account.
 */
export function normalizeDeziMobile(value) {
  let digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  return digits.length === 10 ? digits : "";
}

/**
 * Cart → `get_discounts` request body. Mirrors Android `buildDeziDiscountRequest`.
 *
 * ⚠️ Wire key is `upcCode`, NOT `upc` — a live probe proved a plain `upc` never matches and returns
 * an empty result silently.
 *
 * ⚠️ `price` is the RAW CATALOG unit price (variant-aware), never `orderUnitPrice`: on a first add a
 * line may still carry a transient BOGO/MixMatch-reduced price (deals are only reverted once the
 * dezi result lands), and sending that would both mis-price the lookup and destabilise the dedup key.
 *
 * Only lines with a non-blank (trimmed) upc are sent, and `totalAmount` is the basket OF THOSE ITEMS
 * (Σ price × quantity, 2dp) — not the cart subtotal. Non-UPC lines are invisible to mKonnekt, so
 * including them would only let unrelated products move a threshold deal. `items` empty ⇒ the caller
 * must not ask at all (no discounts, not a failure).
 */
export function buildDeziDiscountRequest({ lines = [], rcn = "", mobileNumber = "", couponCodes = [] }) {
  const items = lines
    .filter((l) => !isSyntheticLine(l))
    .filter(isRegularInventoryProduct)
    .map((l) => ({
      upcCode: deziUpcOf(l),
      quantity: toNum(l.orderCount),
      price: toNum(l.catalogUnitPrice != null ? l.catalogUnitPrice : l.orderUnitPrice),
      name: String(l.orderName || ""),
      categoryName: String((l.model || {}).categoryName || ""),
      unit: String((l.model || {}).uom || ""),
    }));
  return {
    id: "",
    mobileNumber: normalizeDeziMobile(mobileNumber),
    totalAmount: roundHalfUp(items.reduce((s, i) => s + i.price * i.quantity, 0)),
    couponCodes: couponCodes || [],
    merchant: { rcn: String(rcn || ""), loyaltyAtLineItemLevel: true },
    items,
  };
}

/**
 * Stable signature of a request — rcn | UPC-item total | customer phone | items.
 *
 * ⚠️ `mobileNumber` is LOAD-BEARING, not decoration: attaching a customer leaves the cart untouched,
 * so without it the customer-specific (loyalty-funded) discount lookup would be deduped away and
 * never load.
 */
export function deziRequestKey(request) {
  const items = (request.items || []).map((i) => `${i.upcCode}:${i.quantity}:${i.price}`).join(",");
  return `${request.merchant.rcn}|${request.totalAmount}|${request.mobileNumber}|${items}`;
}

/**
 * Normalise a `get_discounts` response. Returns `{ orderDiscounts, itemDiscounts, success }`.
 *
 * EMPTY on ANY failure (non-2xx, missing body, non-200 `status` envelope, missing `result`) so a
 * lookup can never block checkout. `success` distinguishes a CONFIRMED 200 — which may legitimately
 * carry empty arrays — from that fallback; it is read by exactly one thing, the `seenDeziUpcs` UX
 * memory, and by NO money path. Without it one network blip would look like "this upc stopped being
 * dezi" and expire it from the memory.
 */
export function mapDeziDiscounts(ok, body) {
  const EMPTY = { ...DEZI_EMPTY };
  if (!ok || !body) return EMPTY;
  const status = body.status;
  if (status != null && toNum(status) !== 200) return EMPTY;
  const result = body.result || {};
  return {
    orderDiscounts: (result.orderDiscounts || []).filter(Boolean),
    itemDiscounts: (result.itemDiscounts || []).filter(Boolean),
    // Carried for display/diagnostics only — no money path reads them. `upstream_ok` is false when
    // the backend could not reach mKonnekt and answered with an empty result instead.
    promotionMessage: String(result.promotionMessage ?? ""),
    upstream_ok: result.upstream_ok === true,
    // An outage answer (HTTP 200, status 200, upstream_ok false) is NOT a confirmed result: the
    // backend could not reach mKonnekt and substituted an empty one.
    success: result.upstream_ok === true,
  };
}

export const DEZI_EMPTY = {
  orderDiscounts: [],
  itemDiscounts: [],
  promotionMessage: "",
  upstream_ok: false,
  success: false,
};

// ── Sale-time capture for the server-side mKonnekt reporting sweep ──────────────────────────────

/**
 * Attach the request context to a resolved lookup so checkout can report EXACTLY what was applied.
 *
 * The cart slice re-derives the money from whatever result it holds, so the result in the slice IS
 * the one applied at checkout — even when a later lookup for a changed basket is still in flight (or
 * the tender wait timed out). Keeping the quote ON that result object is what guarantees the saved
 * response, phone and rcn always belong together; storing them separately could pair a new phone
 * with an old result.
 *
 * Only a CONFIRMED success carries a quote. A transport failure maps to EMPTY, which is "no quote",
 * not "mKonnekt said nothing applies" — reporting it would tell the sweep a lookup happened.
 */
export function withDeziQuote(result, request, body) {
  if (!result || !result.success || !request) return result;
  const raw = body && body.result && typeof body.result === "object" ? body.result : null;
  return {
    ...result,
    quote: {
      key: deziRequestKey(request),
      mobileNumber: String(request.mobileNumber ?? ""),
      rcn: String((request.merchant || {}).rcn ?? ""),
      // Untouched `result` exactly as Node returned it; the mapped fields only when there was none.
      response: raw || {
        itemDiscounts: result.itemDiscounts,
        orderDiscounts: result.orderDiscounts,
        promotionMessage: result.promotionMessage,
        upstream_ok: result.upstream_ok,
      },
    },
  };
}

/**
 * The three optional saveOrderPos fields (`mkonnekt_discount_response`, `mkonnekt_mobile_number`,
 * `mkonnekt_rcn`). All "" when Dezi is off, no lookup ran, or the lookup failed.
 *
 * ⚠️ Also all "" when the quote was NOT asked for the cart actually being charged: `charged` is that
 * cart (`lines` + the attached customer's `mobileNumber`), and its request key must equal the quote's.
 * After a tender-wait timeout the slice can still hold the previous basket's (or previous customer's)
 * result, and saving that would report a lookup that does not describe this sale. The rcn is taken
 * from the quote — it is a store property, not part of what the cashier can change mid-sale.
 */
export function deziSaveOrderFields(deziDiscounts, charged) {
  const blank = { mkonnekt_discount_response: "", mkonnekt_mobile_number: "", mkonnekt_rcn: "" };
  const q = deziDiscounts && deziDiscounts.success ? deziDiscounts.quote : null;
  if (!q || !charged) return blank;
  const chargedKey = deziRequestKey(
    buildDeziDiscountRequest({ lines: charged.lines || [], rcn: q.rcn, mobileNumber: charged.mobileNumber || "" })
  );
  if (chargedKey !== q.key) return blank;
  let response = "";
  try {
    response = JSON.stringify(q.response) || "";
  } catch {
    response = ""; // never block a sale over a reporting field
  }
  return {
    mkonnekt_discount_response: response,
    mkonnekt_mobile_number: String(q.mobileNumber || ""),
    mkonnekt_rcn: String(q.rcn || ""),
  };
}

/** The upcs carrying a POSITIVE item discount in this result — the sole authority for every gate. */
export function deziUpcSetOf(discounts) {
  const set = new Set();
  for (const d of (discounts || {}).itemDiscounts || []) {
    if (toNum(d.amount) > 0) {
      const u = String(d.itemUPC ?? d.upc ?? "").trim();
      if (u) set.add(u);
    }
  }
  return set;
}

/** Total $ of the mKonnekt ORDER-level discounts (rendered as one synthetic discount row). */
export function deziOrderDiscountTotal(discounts) {
  const sum = ((discounts || {}).orderDiscounts || []).reduce((s, d) => s + toNum(d.amount), 0);
  return Math.max(sum, 0);
}

// ── The money ───────────────────────────────────────────────────────────────────────────────────

/**
 * Split `totalCents` across `capCents` PROPORTIONALLY to the caps, cent-exact:
 * base_i = floor(total × cap_i / Σcap) (never above cap_i), then hand the leftover cent(s) to the
 * largest fractional remainders — ties broken by original order, so the EARLIEST line wins.
 *
 * Contract: `Σ result === min(totalCents, Σcap)` EXACTLY. `totalCents` is coerced to Σcap so a upc
 * total can never exceed the lines' combined value (no negative line).
 *
 * This is what makes 6 identical units each show −$2.00 rather than −$12.00 on the first one — and,
 * because per-line tax is rounded per line, it is also what makes identical units tax identically.
 */
export function allocateProportionalCents(totalCents, capCents) {
  const n = capCents.length;
  const out = new Array(n).fill(0);
  const weightSum = capCents.reduce((s, c) => s + c, 0);
  if (n === 0 || weightSum <= 0 || totalCents <= 0) return out;
  const total = Math.min(totalCents, weightSum);
  const remainder = new Array(n).fill(0);
  let assigned = 0;
  for (let i = 0; i < n; i++) {
    const exact = (total * capCents[i]) / weightSum;
    out[i] = Math.min(Math.floor(exact), capCents[i]);
    remainder[i] = exact - out[i];
    assigned += out[i];
  }
  let leftover = total - assigned;
  const order = out.map((_, i) => i).sort((a, b) => remainder[b] - remainder[a] || a - b); // stable: earliest wins ties
  for (const i of order) {
    if (leftover <= 0) break;
    if (out[i] < capCents[i]) {
      out[i] += 1;
      leftover -= 1;
    }
  }
  return out;
}

/**
 * Inject the mKonnekt ITEM discounts into the cart's per-line discount fields.
 *
 * Writes `deziItemDiscountAmt` (PER UNIT — the money engine multiplies by orderCount), adds it to
 * `orderDiscount`, and records `deziBreakdown` for the Altria reporting node. Called from
 * `recalculate()` AFTER deals have been suppressed/reverted, so every dezi line is at its raw price
 * and the caps are correct.
 *
 * TWO-LEVEL allocation:
 *  • LEVEL 1 = THE MONEY. A upc's amounts are SUMMED (one upc can return several discounts, e.g.
 *    Multi-Can + Loyalty) and split across that upc's matching lines by line value, cent-exact.
 *  • LEVEL 2 = REPORTING ONLY. Each line's ALREADY-ALLOCATED cents are split across the individual
 *    discounts with the same helper, so `Σ breakdown === that line's applied discount` EXACTLY, even
 *    when the headroom clamp bites. ⚠️ It is NESTED, not independent: splitting each discount
 *    independently breaks per-line reconciliation (3 equal lines + two 1¢ discounts → aggregate
 *    [1,1,0] but independent [1,0,0]+[1,0,0] = [2,0,0], and line 0 would claim 2¢ while holding 1¢).
 *
 * ⚠️ `amount` is a LINE TOTAL that SCALES WITH QUANTITY (device-proven: 6 cans → "19.5", 7 → "22.75"),
 * so it is never multiplied by qty; the per-unit value is share ÷ qty.
 *
 * ⚠️ HEADROOM CLAMP (Android Wave 7): a line's share is capped at `(effectiveUnitPrice −
 * existing orderDiscount) × qty` so it can never drive a line negative — there is no floor anywhere
 * in subtotal/tax, so a negative line would silently corrupt both. The proportional WEIGHTS stay
 * line-value on purpose: clamping the weights instead re-weights the split and breaks the cent-exact
 * proofs.
 */
export function applyDeziItemDiscounts(cart, discounts) {
  const byUpc = new Map();
  for (const d of (discounts || {}).itemDiscounts || []) {
    const upc = String(d.itemUPC ?? d.upc ?? "").trim();
    if (!upc || toNum(d.amount) <= 0) continue;
    if (!byUpc.has(upc)) byUpc.set(upc, []);
    byUpc.get(upc).push(d);
  }
  if (byUpc.size === 0) return cart;

  for (const [upc, entries] of byUpc) {
    const upcTotal = entries.reduce((s, e) => s + toNum(e.amount), 0);
    const lines = cart.filter((l) => deziUpcOf(l) === upc && isInjectable(l));
    if (lines.length === 0) continue;

    const capCents = lines.map((l) => Math.round(toNum(l.orderUnitPrice) * Math.max(toNum(l.orderCount), 1) * CENTS));
    const shareCents = allocateProportionalCents(Math.round(upcTotal * CENTS), capCents);
    const entryCents = entries.map((e) => Math.round(toNum(e.amount) * CENTS));

    lines.forEach((line, i) => {
      if (shareCents[i] <= 0) return;
      const qty = Math.max(toNum(line.orderCount), 1);
      const effUnit = toNum(line.orderUnitNewPrice) > 0 ? toNum(line.orderUnitNewPrice) : toNum(line.orderUnitPrice);
      const headroomCents = Math.max(Math.round((effUnit - toNum(line.orderDiscount)) * qty * CENTS), 0);
      const giveCents = Math.min(shareCents[i], headroomCents);
      if (giveCents <= 0) return;
      const perUnit = giveCents / CENTS / qty;
      line.deziItemDiscountAmt = toNum(line.deziItemDiscountAmt) + perUnit;
      line.orderDiscount = toNum(line.orderDiscount) + perUnit;
      // Level 2 — derived from giveCents (POST-clamp) so it can never disagree with the money.
      const entryShare = allocateProportionalCents(giveCents, entryCents);
      const parts = [];
      entries.forEach((e, d) => {
        if (entryShare[d] <= 0) return;
        parts.push({
          id: String(e.id ?? e.discountId ?? ""),
          itemUPC: upc,
          name: String(e.name ?? e.description ?? ""),
          discountType: String(e.discountType ?? ""),
          amount: entryShare[d] / CENTS,
          opCompanyCode: String(e.opCompanyCode ?? ""),
          couponCode: String(e.couponCode ?? ""),
        });
      });
      line.deziBreakdown = (line.deziBreakdown || []).concat(parts);
    });
  }
  return cart;
}

/** Stamp each line's per-upc dezi-ness so the UI can suppress deal labels on ALL same-upc lines. */
export function markDeziUpcLines(cart, deziUpcSet) {
  for (const l of cart) l.isDeziUpcLine = isDeziLine(l, deziUpcSet);
  return cart;
}

/** True when this line's upc carries a live manufacturer discount. Empty set ⇒ always false. */
export function isDeziLine(line, deziUpcSet) {
  if (!deziUpcSet || deziUpcSet.size === 0) return false;
  const upc = deziUpcOf(line);
  return !!upc && deziUpcSet.has(upc);
}

/** True when at least one CART line is a dezi item. Cart-scoped on purpose (a stale set reads false). */
export function hasDeziItems(cart, deziUpcSet) {
  return (cart || []).some((l) => isDeziLine(l, deziUpcSet));
}

// ── The three order-level subtotal helpers ──────────────────────────────────────────────────────
// ⚠️ THREE SIBLINGS, NONE INTERCHANGEABLE — do NOT merge them. Each mirrors the arithmetic of the
// sum it is subtracted from, so the subtraction is apples-to-apples:
//   deziNetSubtotal          NET,   order-level discount BASE (optionally coupon-scoped)
//   deziEligibleGrossSubtotal GROSS, coupon-eligible only  → the coupon-minimum sum
//   deziLineItemGrossSubtotal GROSS, NO coupon filter      → the over-discount net
// Reusing the NET one where a GROSS is wanted leaves the manufacturer discount behind as residue
// ($100 line at −$3.25 ⇒ 100.00 − 96.75 = $3.25 instead of $0.00), and a discount smaller than that
// residue would then survive on an all-dezi cart.

function grossUnit(l) {
  return toNum(l.orderUnitNewPrice) > 0 ? toNum(l.orderUnitNewPrice) : toNum(l.orderUnitPrice);
}

/**
 * NET subtotal contributed by dezi lines — the amount to REMOVE from an order-level discount base so
 * coupons / custom discounts never apply to a manufacturer-discounted item (rule 4). Mirrors
 * calculateSubtotal's per-line math. `couponScoped` additionally drops coupon-excluded lines so the
 * subtraction matches a coupon-eligible base.
 */
export function deziNetSubtotal(cart, deziUpcSet, couponScoped) {
  if (!deziUpcSet || deziUpcSet.size === 0) return 0;
  let subTotal = 0;
  for (const item of cart) {
    if (isSyntheticLine(item)) continue;
    if (!isDeziLine(item, deziUpcSet)) continue;
    if (item.isLotteryTicket === "1") continue;
    if (couponScoped && item.isCouponEligible === false) continue;
    const unitPrice = grossUnit(item);
    const count = toNum(item.orderCount);
    const pct = toNum(item.orderDiscountPercent);
    const discountPrice =
      toNum(item.orderDiscount) > 0 ? toNum(item.orderDiscount) * count : pct > 0 ? Math.trunc(((unitPrice * pct) / 100) * 100) / 100 : 0;
    subTotal += unitPrice * count - discountPrice;
  }
  return subTotal;
}

/** GROSS, coupon-eligible only — subtracted from the coupon-minimum eligible sum. */
export function deziEligibleGrossSubtotal(cart, deziUpcSet) {
  if (!deziUpcSet || deziUpcSet.size === 0) return 0;
  return cart
    .filter((l) => !l.isSplitPaymentDiscount && l.isCouponEligible !== false && l.isLotteryTicket !== "1")
    .filter((l) => isDeziLine(l, deziUpcSet))
    .reduce((s, l) => s + grossUnit(l) * toNum(l.orderCount), 0);
}

/** GROSS, no coupon filter — subtracted from the over-discount net. */
export function deziLineItemGrossSubtotal(cart, deziUpcSet) {
  if (!deziUpcSet || deziUpcSet.size === 0) return 0;
  return cart
    .filter((l) => !l.isSplitPaymentDiscount && l.isLotteryTicket !== "1")
    .filter((l) => isDeziLine(l, deziUpcSet))
    .reduce((s, l) => s + grossUnit(l) * toNum(l.orderCount), 0);
}

/**
 * The `seenDeziUpcs` device memory — upcs mKonnekt has EVER discounted, used ONLY to decide whether
 * the checkout wait / cart-tap gate is warranted while a lookup is in flight.
 *
 * ⚠️ UX ONLY, NEVER MONEY. `deziUpcSet` stays the sole discount authority. It exists because
 * `deziUpcSet` is empty on the FIRST add of a cart, so gating on it alone made a cart of only
 * NON-dezi products wait for a discount that was never coming, while gating it away re-opened
 * "pay at the pre-discount total on a first add".
 *
 * ADD what came back discounted, REMOVE every upc we ASKED about that did not — the removal is what
 * stops it rotting when the backend de-lists a product. Only ever folded in on a CONFIRMED success
 * (see mapDeziDiscounts): expiring on a failed fetch would drop a known dezi upc and let the next
 * sale proceed at the pre-discount total.
 */
export function foldSeenDeziUpcs(seen, discountedUpcs, requestedUpcs) {
  const next = new Set();
  for (const u of seen || []) if (!requestedUpcs.has(u)) next.add(u);
  for (const u of discountedUpcs) next.add(u);
  return next;
}
